import React from "react";
import { base44 } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Shield, Hammer, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const TIMELINE = [
  { year: "2010", event: "Dominic founds Top-Notch Contractors in Dundalk, MD" },
  { year: "2013", event: "Expands to full kitchen remodeling and whole-house renovations" },
  { year: "2016", event: "100th project milestone — celebrated with the Baltimore community" },
  { year: "2019", event: "Dean joins as Project Manager, bringing new client-service standards" },
  { year: "2022", event: "PA Home Improvement Contractor license added (HIC# PA177445)" },
  { year: "2024", event: "500+ completed projects across Baltimore County and beyond" },
];

const VALUES = [
  { icon: Shield, title: "Integrity", desc: "Fixed-price quotes. No surprise change orders. We own our estimates and our mistakes." },
  { icon: Hammer, title: "Craftsmanship", desc: "We care about what's behind the walls as much as what's in front of them." },
  { icon: Heart, title: "Respect", desc: "Your home is your family's space. We show up on time and treat it accordingly." },
];

export default function About() {
  const { data: team = [] } = useQuery({
    queryKey: ["team-members"],
    queryFn: () => base44.entities.TeamMembers.list("display_order"),
    initialData: [],
  });

  return (
    <div className="min-h-screen bg-background pt-0">
      {/* Hero */}
      <section className="bg-primary py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-4">Our Story</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white max-w-2xl leading-tight">
            Built From Craft, Run With Integrity
          </h1>
        </div>
      </section>

      {/* Founder story */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-4">Founder</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-5">Meet Dominic</h2>
            <p className="font-body text-muted-foreground leading-relaxed mb-4">
              Dominic founded Top-Notch Contractors in 2010 with a simple premise: deliver the quality of work you'd want done on your own home. Having come up through the trades — framing, tile, finish carpentry — he understood exactly what quality looked like and what it actually cost to do things right.
            </p>
            <p className="font-body text-muted-foreground leading-relaxed mb-4">
              Over 15 years and more than 500 completed projects across Baltimore County, that standard hasn't changed. Dominic is known for creative problem-solving on tight old-house layouts, for showing up every single day, and for the kind of straight talk that homeowners appreciate when they're making a major investment.
            </p>
            <p className="font-body text-muted-foreground leading-relaxed mb-8">
              Top-Notch is a field-based operation. There's no showroom — Dominic and the team come to you, walk your space, and work from your home, not a sales floor.
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="font-body text-sm bg-secondary border border-border px-4 py-2 rounded-full text-foreground">MHIC# 05-147079</span>
              <span className="font-body text-sm bg-secondary border border-border px-4 py-2 rounded-full text-foreground">PA HIC# PA177445</span>
              <span className="font-body text-sm bg-secondary border border-border px-4 py-2 rounded-full text-foreground">Licensed &amp; Insured</span>
              <span className="font-body text-sm bg-secondary border border-border px-4 py-2 rounded-full text-foreground">Est. 2010</span>
            </div>
          </div>
          <div className="bg-secondary/50 rounded-2xl p-10 border border-border flex flex-col items-center justify-center text-center">
            <div className="w-48 h-48 mb-5 overflow-hidden rounded-xl shadow-lg">
              <img src="https://media.base44.com/images/public/69df73aa4db172ca923f00d6/36668680f_Profile.JPG" alt="Dominic — Founder & Lead Contractor" className="w-full h-full object-cover object-center scale-75" />
            </div>
            <div className="font-heading text-2xl font-bold text-foreground mb-1">Dominic</div>
            <div className="font-body text-muted-foreground">Founder &amp; Lead Contractor</div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-primary py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {[
            { value: "2010", label: "Year Founded" },
            { value: "500+", label: "Projects Completed" },
            { value: "15+", label: "Years Experience" },
            { value: "MD+PA", label: "Licensed In" },
          ].map((s) => (
            <div key={s.label}>
              <div className="font-heading text-4xl font-bold text-accent mb-1">{s.value}</div>
              <div className="font-body text-sm text-white/70">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-3">Our History</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">Company Timeline</h2>
        </div>
        <div className="space-y-6">
          {TIMELINE.map((item, i) => (
            <motion.div
              key={item.year}
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className="flex gap-6 items-start"
            >
              <div className="font-heading text-xl font-bold text-accent w-16 flex-shrink-0 pt-0.5">{item.year}</div>
              <div className="flex-1 pb-6 border-b border-border last:border-0">
                <p className="font-body text-foreground leading-relaxed">{item.event}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-3">Our Values</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">Why Baltimore Trusts Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-card rounded-xl p-8 border border-border"
              >
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-5">
                  <v.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">{v.title}</h3>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      {team.length > 0 && (
        <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-3">The Team</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">The People Behind the Work</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {team.map((member) => (
              <div key={member.id} className="bg-card rounded-xl p-6 border border-border text-center">
                {member.photo_url && (
                  <div className="w-20 h-20 mx-auto bg-secondary rounded-full overflow-hidden mb-4">
                    <img loading="lazy" decoding="async" src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <h3 className="font-heading text-lg font-bold text-foreground mb-0.5">{member.name}</h3>
                <p className="font-body text-xs font-semibold text-accent uppercase tracking-wide mb-3">{member.role}</p>
                {member.bio && member.bio !== "Photo and bio pending — real team member to be added by Yoni." && (
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">{member.bio}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Meet the Team in Your Home</h2>
          <p className="font-body text-white/70 mb-8">Schedule a free consultation and see why Baltimore homeowners keep calling us back.</p>
          <Link to="/contact">
            <Button size="lg" className="bg-accent text-white hover:bg-accent/90 font-body font-semibold px-10">
              Book a Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}